import React, { useEffect, useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import type { ItinerarySheetRow, ItinerarySheetsModalData } from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
	'&.MuiCheckbox-indeterminate': {
		color: '#00A5FF',
	},
}

const checkboxLabelSx = {
	margin: 0,
	'.MuiTypography-root': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: '#092056',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(0, 165, 255, 0.3)',
	borderRadius: '6px',
	height: '24px',
	'& .MuiToggleButton-root': {
		border: 'none',
		padding: '2px',
		width: '24px',
		height: '24px',
		color: '#00A5FF',
		'&.Mui-selected': {
			backgroundColor: 'rgba(0, 165, 255, 0.15)',
			color: '#00A5FF',
		},
		'&:hover': {
			backgroundColor: 'rgba(0, 165, 255, 0.08)',
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiCheckbox-root': {
		color: '#8695AF',
		'&.Mui-checked': {
			color: '#00A5FF',
		},
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultRows: ItinerarySheetRow[] = [
	{
		id: 'it-1',
		motorista: 'Beatriz Silva',
		ext: 'Não',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '11:30',
		localInicio: 'Porto',
		localTermino: 'Vila Nova de Gaia',
		horaTermino: '12:30',
		dataTermino: '02/05/2024',
		servico: '0090001',
	},
	{
		id: 'it-2',
		motorista: 'Carlos Pereira',
		ext: 'Sim',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '14:00',
		localInicio: 'Coimbra',
		localTermino: 'Figueira da Foz',
		horaTermino: '15:00',
		dataTermino: '02/05/2024',
		servico: '0090002',
	},
	{
		id: 'it-3',
		motorista: 'Daniela Martins',
		ext: 'Sim',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '08:45',
		localInicio: 'Lisboa',
		localTermino: 'Queluz',
		horaTermino: '09:30',
		dataTermino: '02/05/2024',
		servico: '0090003',
	},
	{
		id: 'it-4',
		motorista: 'Eduardo Lima',
		ext: 'Não',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '10:15',
		localInicio: 'Braga',
		localTermino: 'Guimarães',
		horaTermino: '11:00',
		dataTermino: '02/05/2024',
		servico: '0090004',
	},
	{
		id: 'it-5',
		motorista: 'Fernanda Costa',
		ext: 'Sim',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '13:00',
		localInicio: 'Faro',
		localTermino: 'Tavira',
		horaTermino: '13:45',
		dataTermino: '02/05/2024',
		servico: '0090005',
	},
	{
		id: 'it-6',
		motorista: 'Gustavo Soares',
		ext: 'Não',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '09:30',
		localInicio: 'Setúbal',
		localTermino: 'Sesimbra',
		horaTermino: '10:15',
		dataTermino: '02/05/2024',
		servico: '0090006',
	},
	{
		id: 'it-7',
		motorista: 'Helena Ribeiro',
		ext: 'Sim',
		empresa: 'Transol',
		dataInicio: '02/05/2024',
		horaInicio: '16:00',
		localInicio: 'Évora',
		localTermino: 'Montemor-o-Novo',
		horaTermino: '17:00',
		dataTermino: '02/05/2024',
		servico: '0090007',
	},
]

const driverColumn: GridColDef<ItinerarySheetRow> = {
	field: 'motorista',
	headerName: 'Motorista',
	width: 200,
	renderCell: params => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
			<Avatar
				src={params.row.avatarUrl}
				sx={{
					borderRadius: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: 'lightgrey 50% / cover no-repeat',
					width: 32,
					height: 32,
					fontSize: '20px',
					fontWeight: 400,
					lineHeight: 1,
				}}
			>
				{params.value?.[0] ?? 'M'}
			</Avatar>
			<Typography sx={{ fontSize: '12px', color: '#092056' }}>{params.value}</Typography>
		</Box>
	),
}

const CustomModalItinerarySheets: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as ItinerarySheetsModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []
	const [density, setDensity] = useState<DensityMode>('compact')
	const [includeExternal, setIncludeExternal] = useState(true)
	const [includeInternal, setIncludeInternal] = useState(true)

	const rows = useMemo(() => (data?.rows?.length ? data.rows : defaultRows), [data])

	useEffect(() => {
		if (!data) {
			return
		}
		setIncludeExternal(data.includeExternal ?? true)
		setIncludeInternal(data.includeInternal ?? true)
	}, [data])

	const filteredRows = useMemo(
		() =>
			rows.filter(row => {
				if (row.ext === 'Sim' && !includeExternal) {
					return false
				}
				if (row.ext === 'Não' && !includeInternal) {
					return false
				}
				return true
			}),
		[rows, includeExternal, includeInternal],
	)

	const columns = useMemo<GridColDef<ItinerarySheetRow>[]>(
		() => [
			driverColumn,
			{ field: 'ext', headerName: 'Ext', width: 63 },
			{ field: 'empresa', headerName: 'Empresa', width: 88 },
			{ field: 'dataInicio', headerName: 'Data Início', width: 100 },
			{ field: 'horaInicio', headerName: 'Hora Início', width: 99 },
			{ field: 'localInicio', headerName: 'Local Início', width: 102 },
			{ field: 'localTermino', headerName: 'Local Término', width: 120 },
			{ field: 'horaTermino', headerName: 'Hora Término', width: 117 },
			{ field: 'dataTermino', headerName: 'Data Término', width: 117 },
			{ field: 'servico', headerName: 'Serviço', width: 78 },
		],
		[],
	)

	const initialSelection = useMemo(() => filteredRows.slice(0, 3).map(row => row.id), [filteredRows])
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(initialSelection)

	useEffect(() => {
		setSelectionModel(initialSelection)
	}, [initialSelection])

	const selectedCount = selectionModel.length
	const referenceDate = data?.date ?? rows[0]?.dataInicio ?? '02/05/2024'

	const handlePrintAction = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(filteredRows)
			return
		}
		console.log('Imprimir folhas de itinerário', filteredRows)
	}

	const handleSendAction = () => {
		if (typeof handleSend === 'function') {
			handleSend(filteredRows)
			return
		}
		console.log('Enviar folhas de itinerário', filteredRows)
	}

	if (!data) {
		return null
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					height: '582px',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box
				sx={{
					fontFamily: 'Poppins, sans-serif',
					color: '#092056',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Box
					sx={{
						padding: '16px 24px 8px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Folhas de Itinerário
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={includeExternal}
									onChange={event => setIncludeExternal(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Externos'
							sx={checkboxLabelSx}
						/>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={includeInternal}
									onChange={event => setIncludeInternal(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Da casa'
							sx={checkboxLabelSx}
						/>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '0 24px 12px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
							{`Folhas de Itinerário de ${referenceDate}`}
						</Typography>
						<Box
							sx={{
								width: '1px',
								height: '16px',
								backgroundColor: 'rgba(134, 149, 175, 0.4)',
							}}
						/>
						<Typography sx={{ fontSize: '12px', color: '#8695AF' }}>{`${selectedCount} selecionado(s)`}</Typography>
					</Box>
					<ToggleButtonGroup
						exclusive
						value={density}
						onChange={(_, value) => value && setDensity(value)}
						sx={toggleGroupSx}
					>
						<ToggleButton
							value='compact'
							aria-label='Compacto'
						>
							<TableRowsIcon sx={{ fontSize: '16px' }} />
						</ToggleButton>
						<ToggleButton
							value='standard'
							aria-label='Espaçado'
						>
							<ViewHeadlineIcon sx={{ fontSize: '16px' }} />
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>

				<Box sx={{ padding: '0 24px', flex: 1 }}>
					<Box sx={{ height: 322 }}>
						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={filteredRows}
							columns={columns}
							checkboxSelection
							disableRowSelectionOnClick
							density={density}
							rowSelectionModel={selectionModel}
							onRowSelectionModelChange={model => setSelectionModel(model)}
							rowHeight={density === 'compact' ? 32 : 40}
							columnHeaderHeight={54}
							pagination
							pageSizeOptions={[5, 10]}
							hideFooterSelectedRowCount
							sx={{ ...tableSx, ...dataGridDensityTransitionSx }}
						/>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '12px 24px 16px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '8px',
					}}
				>
					<CustomButton
						variant='text'
						size='medium'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						size='medium'
						onClick={handlePrintAction}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>
					<CustomButton
						variant='contained'
						size='medium'
						onClick={handleSendAction}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalItinerarySheets
